/**
 * Document to Markdown Tool
 *
 * Upload any supported document and convert it to markdown format.
 * Supports multiple methods:
 * - Mistral OCR: High-quality document-to-markdown conversion
 * - Simple conversion: Fast markdown conversion using external service
 */

import { CopyOutlined, PlayCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { Markdown } from '@client/components/_ui/display/Markdown';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import type { CollapseProps, UploadFile } from 'antd';
import { Alert, Button, Card, Collapse, Descriptions, Typography, Upload } from 'antd';
import { FileText, ScanEye } from 'lucide-react';
import { useCallback, useState } from 'react';

const { Text } = Typography;

const ACCEPTED_FILE_TYPES =
    '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.html,.htm,.xml,.csv,.json,.png,.jpg,.jpeg,.gif,.bmp,.webp,.txt,.rtf';

type ConversionMethod = 'simple' | 'mistralOcr';

export const Route = createFileRoute('/admin-console/tools/document-to-markdown')({
    staticData: {
        ui: {
            title: 'Document to markdown',
            menuDescription: 'Convert documents to markdown format',
        },
        breadcrumb: { title: 'Document to markdown' },
    },
    component: DocumentToMarkdownPage,
});

interface SimpleConversionResult {
    success: boolean;
    filename?: string;
    fileSize?: number;
    markdown?: string;
    markdownLength?: number;
    conversionMs?: number;
    supportedExtensions?: readonly string[];
    error?: string;
    testedAt?: string;
}

type MistralOcrResult = RouterOutputs['test']['mistralOcrConversionTest'];

function DocumentToMarkdownPage() {
    const { trpc } = useTrpc();

    const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
    const [method, setMethod] = useState<ConversionMethod>('mistralOcr');
    const [simpleResult, setSimpleResult] = useState<SimpleConversionResult | null>(null);
    const [mistralOcrResult, setMistralOcrResult] = useState<MistralOcrResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const documentUploadMutation = trpc.test.documentUploadTest.useMutation();
    const mistralOcrConversionMutation = trpc.test.mistralOcrConversionTest.useMutation();

    const handleCopy = useCallback(async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            message.success('Copied to clipboard');
        } catch {
            message.error('Failed to copy');
        }
    }, []);

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleConvert = useCallback(async () => {
        if (!uploadedFile?.originFileObj) {
            setError('Please select a file first');
            return;
        }

        setLoading(true);
        setError(null);
        setSimpleResult(null);
        setMistralOcrResult(null);

        try {
            const fileContent = await fileToBase64(uploadedFile.originFileObj);

            if (method === 'simple') {
                const response = await documentUploadMutation.mutateAsync({
                    fileContent,
                    fileName: uploadedFile.name,
                });
                setSimpleResult(response);
            } else if (method === 'mistralOcr') {
                const response = await mistralOcrConversionMutation.mutateAsync({
                    fileContent,
                    fileName: uploadedFile.name,
                });
                setMistralOcrResult(response);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [uploadedFile, method, documentUploadMutation, mistralOcrConversionMutation]);

    const renderSimpleResult = () => {
        if (!simpleResult) return null;

        if (simpleResult.error) {
            return <Alert type="error" title={simpleResult.error} showIcon className="mt-4" />;
        }

        if (!simpleResult.success || !simpleResult.markdown) {
            return <Alert type="error" title="Conversion failed" showIcon className="mt-4" />;
        }

        return (
            <div className="mt-4">
                <Collapse
                    defaultActiveKey={['formatted']}
                    items={[
                        {
                            key: 'stats',
                            label: 'Processing stats',
                            children: (
                                <Descriptions size="small" column={2} bordered>
                                    <Descriptions.Item label="Filename">{simpleResult.filename}</Descriptions.Item>
                                    <Descriptions.Item label="File size">
                                        {simpleResult.fileSize
                                            ? `${(simpleResult.fileSize / 1024).toFixed(1)} KB`
                                            : 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Markdown length">
                                        {simpleResult.markdownLength?.toLocaleString()} characters
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Conversion time">
                                        {simpleResult.conversionMs}ms
                                    </Descriptions.Item>
                                </Descriptions>
                            ),
                        },
                        {
                            key: 'formatted',
                            label: 'Formatted markdown',
                            extra: (
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(simpleResult.markdown ?? '');
                                    }}
                                    size="small"
                                >
                                    Copy
                                </Button>
                            ),
                            children: (
                                <div className="rounded border border-separator bg-card p-4">
                                    <Markdown variant="document">{simpleResult.markdown}</Markdown>
                                </div>
                            ),
                        },
                        {
                            key: 'raw',
                            label: 'Raw markdown',
                            extra: (
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(simpleResult.markdown ?? '');
                                    }}
                                    size="small"
                                >
                                    Copy
                                </Button>
                            ),
                            children: (
                                <div className="max-h-96 overflow-auto rounded border border-separator bg-muted p-3">
                                    <pre className="whitespace-pre-wrap text-xs">{simpleResult.markdown}</pre>
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    };

    const renderMistralOcrResult = () => {
        if (!mistralOcrResult) return null;

        if (mistralOcrResult.error) {
            return <Alert type="error" title={mistralOcrResult.error} showIcon className="mt-4" />;
        }

        if (!mistralOcrResult.success || !mistralOcrResult.markdown) {
            return <Alert type="error" title="Conversion failed" showIcon className="mt-4" />;
        }

        const collapseItems: CollapseProps['items'] = [
            {
                key: 'stats',
                label: 'Processing stats',
                children: (
                    <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="Filename">{mistralOcrResult.filename}</Descriptions.Item>
                        <Descriptions.Item label="File size">
                            {mistralOcrResult.fileSize ? `${(mistralOcrResult.fileSize / 1024).toFixed(1)} KB` : 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Markdown length">
                            {mistralOcrResult.markdownLength?.toLocaleString()} characters
                        </Descriptions.Item>
                        {mistralOcrResult.metadata && (
                            <>
                                <Descriptions.Item label="Processing time">
                                    {mistralOcrResult.metadata.processingTimeMs}ms
                                </Descriptions.Item>
                                <Descriptions.Item label="OCR provider time">
                                    {mistralOcrResult.metadata.providerProcessingTimeMs}ms
                                </Descriptions.Item>
                                <Descriptions.Item label="Page count">
                                    {mistralOcrResult.metadata.pageCount}
                                </Descriptions.Item>
                                <Descriptions.Item label="Document size">
                                    {(mistralOcrResult.metadata.docSizeBytes / 1024).toFixed(1)} KB
                                </Descriptions.Item>
                                <Descriptions.Item label="Model">{mistralOcrResult.metadata.model}</Descriptions.Item>
                            </>
                        )}
                    </Descriptions>
                ),
            },
            {
                key: 'formatted',
                label: 'Formatted markdown',
                extra: (
                    <Button
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(mistralOcrResult.markdown ?? '');
                        }}
                        size="small"
                    >
                        Copy
                    </Button>
                ),
                children: (
                    <div className="rounded border border-separator bg-card p-4">
                        <Markdown variant="document">{mistralOcrResult.markdown}</Markdown>
                    </div>
                ),
            },
            {
                key: 'raw',
                label: 'Raw markdown',
                extra: (
                    <Button
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(mistralOcrResult.markdown ?? '');
                        }}
                        size="small"
                    >
                        Copy
                    </Button>
                ),
                children: (
                    <div className="max-h-96 overflow-auto rounded border border-separator bg-muted p-3">
                        <pre className="whitespace-pre-wrap text-xs">{mistralOcrResult.markdown}</pre>
                    </div>
                ),
            },
        ];

        // Add per-page breakdown if available
        if (mistralOcrResult.pageBreakdown && mistralOcrResult.pageBreakdown.length > 1) {
            collapseItems.push({
                key: 'pages',
                label: `Page breakdown (${mistralOcrResult.pageBreakdown.length} pages)`,
                children: (
                    <Descriptions size="small" column={3} bordered>
                        {mistralOcrResult.pageBreakdown.map((page) => (
                            <Descriptions.Item key={page.index} label={`Page ${page.index + 1}`}>
                                {page.markdownLength.toLocaleString()} chars
                                {page.imageCount > 0 && `, ${page.imageCount} image${page.imageCount > 1 ? 's' : ''}`}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                ),
            });
        }

        return (
            <div className="mt-4">
                <Collapse defaultActiveKey={['formatted']} items={collapseItems} />
            </div>
        );
    };

    const renderResult = () => {
        if (error) {
            return <Alert type="error" title={error} showIcon className="mt-4" />;
        }

        if (method === 'simple') {
            return renderSimpleResult();
        }

        return renderMistralOcrResult();
    };

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-4xl p-6">
                <Card
                    title={
                        <Space>
                            <FileText size={16} />
                            <span>Document upload to markdown</span>
                        </Space>
                    }
                    extra={
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            loading={loading}
                            onClick={handleConvert}
                            disabled={!uploadedFile}
                        >
                            Run
                        </Button>
                    }
                >
                    <Text type="secondary">Upload any document and convert to markdown</Text>

                    <div className="mt-3">
                        <Upload
                            accept={ACCEPTED_FILE_TYPES}
                            maxCount={1}
                            fileList={uploadedFile ? [uploadedFile] : []}
                            beforeUpload={(file) => {
                                setUploadedFile({
                                    uid: '-1',
                                    name: file.name,
                                    status: 'done',
                                    originFileObj: file,
                                });
                                setSimpleResult(null);

                                setMistralOcrResult(null);
                                setError(null);
                                return false;
                            }}
                            onRemove={() => {
                                setUploadedFile(null);
                                setSimpleResult(null);

                                setMistralOcrResult(null);
                                setError(null);
                            }}
                        >
                            <Button icon={<UploadOutlined />}>Select file</Button>
                        </Upload>
                        <Text type="secondary" className="mt-1 block text-xs">
                            Supported: {ACCEPTED_FILE_TYPES}
                        </Text>
                    </div>

                    <div className="mt-4">
                        <Text strong className="mb-2 block">
                            Conversion method
                        </Text>
                        <SegmentedControl
                            value={method}
                            onChange={(value) => {
                                setMethod(value);
                                setSimpleResult(null);
                                setMistralOcrResult(null);
                                setError(null);
                            }}
                            options={[
                                { value: 'mistralOcr', label: 'Mistral OCR', icon: <ScanEye size={14} /> },
                                { value: 'simple', label: 'Simple conversion', icon: <FileText size={14} /> },
                            ]}
                            aria-label="Conversion method"
                        />
                        <Text type="secondary" className="mt-1 block text-xs">
                            {method === 'simple'
                                ? 'Fast, basic markdown conversion'
                                : 'Mistral OCR API with per-page results and high-quality document conversion'}
                        </Text>
                    </div>

                    {renderResult()}
                </Card>
            </div>
        </PageController>
    );
}
